import React from 'react'
import { useParams } from 'react-router-dom'

import { analysisFamilyLabel } from '../features/orders/analysisName'
import CaseWorkspace from './CaseWorkspace'

/**
 * Full case-style view for an order resolved from a SeqOne analysis token
 * (GEN-5569). Renders the shared CaseWorkspace in order mode: all delivered
 * members' CRAM/VCF tracks in IGV plus the variant table and facets, backed by
 * the /orders/{token}/* endpoints. The variant hooks already accept a basePath
 * (GEN-5495), so this reuses them against `/orders/{token}` unchanged.
 *
 * Replaces the IGV-only OrderViewerPage for the primary order route; that page
 * remains for any callers that still want the tracks-only view.
 */
const OrderCasePage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const orderToken = token ? decodeURIComponent(token) : ''
  const base = `/orders/${encodeURIComponent(orderToken)}`
  // When the token is a SeqOne analysis display name, show its declared family
  // type as a badge (GEN-5657). Informational only; all delivered members are
  // still shown until analysis-cohort scoping lands (GEN-5658).
  const familyLabel = analysisFamilyLabel(orderToken) ?? undefined

  return (
    <CaseWorkspace
      displayId={orderToken}
      kind="order"
      basePath={base}
      tracksPath={`${base}/tracks`}
      variantLookupPath={(variantId, track) => {
        const trackQuery = track ? `?track=${encodeURIComponent(track)}` : ''
        return `${base}/variants/${encodeURIComponent(variantId)}${trackQuery}`
      }}
      backTo="/home"
      backLabel="Back to orders"
      titleKicker="Order"
      subtitle={familyLabel}
    />
  )
}

export default OrderCasePage
